<ng-template #recursiveForm let-panel>
  <div
    class="form-panel"
    [ngClass]="panel?.panelClass"
    [ngStyle]="panelStyles[panel.panelName]"
  >
    @if (panel.panelLabel?.length > 0) {
      <h5>{{ panel.panelLabel }}</h5>
    }

    <!-- Iterate through form items within the panel -->
    @if (panel.items?.length > 0) {
      <app-dynamic-form
        [formProperties]="panel.items"
        [formGroup]="formGroup"
        [fieldFilterString]="searchQuery"
        [isFilterEnabled]="false"
        [searchString$]="searchString$"
      ></app-dynamic-form>
    }

    <!-- Recursively render child panels using ngTemplateOutlet -->
    @if (panel.panels?.length > 0) {
      @for (childPanel of panel.panels; track childPanel) {
        <ng-container
          *ngTemplateOutlet="recursiveForm; context: { $implicit: childPanel }"
        ></ng-container>
      }
    }
  </div>
</ng-template>

<div class="form-editor-container">
  <div class="form-editor">
    <!-- ⚠️ Tabs - Portal Definition -->
    <ng-template cdkPortal #tabsTemplate>
      <div class="form-tabs-container">
        <app-search-input
          [(ngModel)]="searchQuery"
          (ngModelChange)="filterTabs()"
        ></app-search-input>

        <!-- Display the tabs if any are available -->
        @if (filteredTabs.length > 0) {
          <ul class="form-tabs">
            @for (tab of filteredTabs; track tab; let i = $index) {
              <li>
                <button
                  type="button"
                  [class.active]="selectedTabKey === tab.key"
                  [class.tab-error]="tab.countErrors > 0"
                  (click)="setActiveTab(tab)"
                >
                  <div class="tab-label">
                    @if (tab.icon) {
                      <fa-icon [icon]="tab.icon"></fa-icon>
                    }
                    {{ tab.label }}
                  </div>
                  <!-- Display the error count if any -->
                  @if (tab.countErrors > 0) {
                    <span class="error-count">
                      <fa-icon [icon]="['fas', 'warning']"></fa-icon>
                      {{ tab.countErrors }}
                    </span>
                  }
                </button>
              </li>
            }
          </ul>
        }
      </div>
    </ng-template>

    <!-- ⚠️ Form Panels - Portal Definition -->
    <ng-template cdkPortal #formTemplate>
      @if (formConfig?.tabs[activeTabIndex]) {
        <div class="form-tab-content">
          <!-- A waiting dialog shows progress on its Save button and keeps the form on screen (WAP-1857) -->
          @if ((saving$ | async) || (mutationSaving() && !dialogInlineError)) {
            <div class="form-panels">
              <app-loading [size]="'4x'"></app-loading>
            </div>
          } @else {
            <div
              class="form-panels"
              [ngClass]="formConfig?.tabs[activeTabIndex]?.formPanelsClass"
            >
              <!-- Eager loading: render ALL tabs, hide inactive ones with display:none -->
              @if (shouldEagerLoadComponents()) {
                @for (tab of formConfig?.tabs; track tab) {
                  <div
                    [style.display]="
                      activeTabIndex === tab.tabIndex ? 'contents' : 'none'
                    "
                  >
                    @for (panel of tab?.panels; track panel) {
                      <ng-container
                        *ngTemplateOutlet="
                          recursiveForm;
                          context: { $implicit: panel }
                        "
                      ></ng-container>
                    }
                  </div>
                }
              }
              <!-- Lazy loading: only render active tab (default behavior) -->
              @if (!shouldEagerLoadComponents()) {
                @for (
                  panel of formConfig?.tabs[activeTabIndex]?.panels;
                  track panel
                ) {
                  <ng-container
                    *ngTemplateOutlet="
                      recursiveForm;
                      context: { $implicit: panel }
                    "
                  ></ng-container>
                }
              }
              @if (filteredTabs.length === 0) {
                <div class="sui-dialog-tabs__empty">
                  {{ "tab-dialog.no-results-message" | translate }}
                </div>
              }
            </div>
            @if (!isPopupEditor) {
              <div class="form-toolbar">
                <sui-button
                  variant="secondary"
                  [leadingIcon]="['fas', 'times']"
                  (click)="onCancel()"
                >
                  {{ "btn.cancel" | translate }}
                </sui-button>
                <sui-button
                  variant="primary"
                  [leadingIcon]="['fas', 'save']"
                  (click)="submit()"
                >
                  {{ "btn.save" | translate }}
                </sui-button>
              </div>
            }
          }
        </div>
      }
    </ng-template>

    <!-- ⚠️ Inline fallback rendering when not projected - displayed in the Host Component -->
    @if (!isPopupEditor) {
      <ng-container *ngTemplateOutlet="tabsTemplate"></ng-container>
      <ng-container *ngTemplateOutlet="formTemplate"></ng-container>
    }
  </div>
</div>
